import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { FinanceClient } from '../pages/DashboardPage/views/FinancesView/masters/financeMasters';
import { paymentMethodCode, paymentMethodName } from './paymentMethodCodes';
import { getAccessToken, buildQuery, unwrapPage, unwrapRecord, unwrapError, getTransportError } from './apiUtils';

const paymentTermsName = (days: number): string => (days === 0 ? 'Inmediato (Contado)' : `Neto ${days} días`);

/**
 * La tabla `clients` solo guarda nombre, CIF, email, teléfono, dirección, forma y
 * plazo de pago, descuento, límite, IVA, estado y notas. Dirección desglosada,
 * persona de contacto, idioma y adjuntos son del maestro de
 * Financiero y quedan con valores neutros.
 */
export const mapFinanceClient = (raw: any): FinanceClient => {
  const days = Number(raw.payment_terms_days);
  return {
    id:                     String(raw.id),
    name:                   raw.name || '',
    contactPerson:          '',
    email:                  raw.email || '',
    cif:                    raw.cif || '',
    phone:                  raw.phone || '',
    street:                 raw.address || '',
    city:                   '',
    postalCode:             '',
    province:               '',
    country:                'España',
    account:                raw.account_code || '430000',
    paymentTerms:           paymentTermsName(isNaN(days) ? 30 : days),
    preferredPaymentMethod: paymentMethodName(raw.payment_method),
    billingLanguage:        'Español',
    notes:                  raw.notes || '',
    attachments:            [],
    balance:                0,
    status:                 raw.status === 'inactive' ? 'Inactivo' : 'Activo'
  };
};

/** Clientes reales desde /client-list (paginado, se recorren todas las páginas). */
export const listFinanceClients = async (): Promise<FinanceClient[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const all: FinanceClient[] = [];
  const pageSize = 100;
  for (let page = 1; page <= 50; page++) {
    const res = await ObexRequestsHandlerP80.get(`/client-list${buildQuery({ access_token: token, page, page_size: pageSize })}`);
    const transportError = getTransportError(res);
    if (transportError) throw new Error(transportError);
    const { rows, total } = unwrapPage(res);
    all.push(...rows.map(mapFinanceClient));
    if (rows.length < pageSize || all.length >= total) break;
  }
  return all;
};

interface FinanceClientPayload {
  name: string;
  cif: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
  status: FinanceClient['status'];
  preferredPaymentMethod: string;
  account: string;
}

const clientQuery = (c: FinanceClientPayload, paymentTermsDays: number) => ({
  name:               c.name,
  cif:                c.cif,
  email:              c.email,
  phone:              c.phone,
  address:            c.address,
  notes:              c.notes,
  status:             c.status === 'Inactivo' ? 'inactive' : 'active',
  payment_method:     paymentMethodCode(c.preferredPaymentMethod),
  payment_terms_days: paymentTermsDays,
  account_code:       c.account
});

/** Alta en clients-create (name y cif obligatorios; el CIF repetido lo rechaza el backend con 409). */
export const createFinanceClient = async (client: FinanceClientPayload, paymentTermsDays: number): Promise<FinanceClient> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  const res = await ObexRequestsHandlerP80.get(`/clients-create${buildQuery({ access_token: token, ...clientQuery(client, paymentTermsDays) })}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al dar de alta el cliente.'));
  return mapFinanceClient(record);
};

/** Modificación en clients-update: solo toca los campos que llegan, así que no pisa descuento, límite ni IVA. */
export const updateFinanceClient = async (id: string, client: FinanceClientPayload, paymentTermsDays: number): Promise<FinanceClient> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  const res = await ObexRequestsHandlerP80.get(`/clients-update${buildQuery({ access_token: token, client_id: id, ...clientQuery(client, paymentTermsDays) })}`);
  const record = unwrapRecord(res);
  if (!record) {
    // Si el servidor no devuelve un texto de error ni la ficha, se enseña lo recibido para poder diagnosticarlo
    const fallback = 'Error al modificar el cliente.';
    const message = unwrapError(res, fallback);
    let raw = '';
    try { raw = JSON.stringify(res); } catch { raw = String(res); }
    throw new Error(message === fallback ? `${fallback} Respuesta del servidor: ${raw.slice(0, 300)}` : message);
  }
  return mapFinanceClient(record);
};
